import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { CustomAgentBuilderToolCall } from '@atbash-labs/ontology-agent-wire'

/**
 * One slice instance feeds the custom-agent builder LLM tab for standalone
 * sessions and tracks its streamed tool-call history.
 */

export interface CustomAgentConfigAgentHistoryItem {
  id: string
  instruction: string
  completed: boolean
  message: string
  affectedCustomAgentConfigIds: string[]
  toolCalls: CustomAgentBuilderToolCall[]
  createdAt: string
}

export interface LiveCustomAgentConfigAgentToolCall {
  id: string
  name: string
  input: unknown
  output: unknown
  status: 'running' | 'completed'
}

export interface CustomAgentConfigAgentSliceState {
  instructionDraft: string
  history: CustomAgentConfigAgentHistoryItem[]
  isStreaming: boolean
  activeInstruction: string | null
  liveToolCalls: LiveCustomAgentConfigAgentToolCall[]
  streamError: string | null
}

const initialState: CustomAgentConfigAgentSliceState = {
  instructionDraft: '',
  history: [],
  isStreaming: false,
  activeInstruction: null,
  liveToolCalls: [],
  streamError: null
}

const customAgentConfigAgentSlice = createSlice({
  name: 'customAgentConfigAgent',
  initialState,
  reducers: {
    setCustomAgentConfigAgentInstructionDraft(state, action: PayloadAction<string>) {
      state.instructionDraft = action.payload
    },
    addCustomAgentConfigAgentHistoryItem(
      state,
      action: PayloadAction<CustomAgentConfigAgentHistoryItem>
    ) {
      state.history.unshift(action.payload)
    },
    clearCustomAgentConfigAgentHistory(state) {
      state.history = []
    },
    startCustomAgentConfigAgentSession(state, action: PayloadAction<string>) {
      state.isStreaming = true
      state.activeInstruction = action.payload
      state.liveToolCalls = []
      state.streamError = null
    },
    upsertLiveCustomAgentConfigAgentToolCall(
      state,
      action: PayloadAction<LiveCustomAgentConfigAgentToolCall>
    ) {
      const existingIndex = state.liveToolCalls.findIndex(
        (toolCall) => toolCall.id === action.payload.id
      )
      if (existingIndex >= 0) {
        state.liveToolCalls[existingIndex] = action.payload
        return
      }
      state.liveToolCalls.push(action.payload)
    },
    failCustomAgentConfigAgentSession(state, action: PayloadAction<string>) {
      state.isStreaming = false
      state.streamError = action.payload
    },
    finishCustomAgentConfigAgentSession(state) {
      state.isStreaming = false
      state.activeInstruction = null
      state.liveToolCalls = []
      state.streamError = null
    }
  }
})

export const {
  setCustomAgentConfigAgentInstructionDraft,
  addCustomAgentConfigAgentHistoryItem,
  clearCustomAgentConfigAgentHistory,
  startCustomAgentConfigAgentSession,
  upsertLiveCustomAgentConfigAgentToolCall,
  failCustomAgentConfigAgentSession,
  finishCustomAgentConfigAgentSession
} = customAgentConfigAgentSlice.actions

export default customAgentConfigAgentSlice.reducer
